/**
 * Track Record Section: stacked circles layout, static final SVG state,
 * hover effects for circles (partner image reveal on circle hover).
 * Uses GSAP (no scroll-driven animation).
 */

(function() {
	'use strict';

	let initAttempts = 0;
	const maxInitAttempts = 50;
	/** Vertical nudge (px): keeps dot center on stroke after GSAP owns transform. */
	const TRACK_RECORD_POINT_LIFT_PX = 2;

	/**
	 * Apply the same SVG + transform state as the end of the former scroll timeline:
	 * all circles visible (first circle stage5, others stage2), all points visible.
	 */
	function applyTrackRecordFinalVisualState(section, pointLiftPx) {
		const circles = section.querySelectorAll('.track-record-circle');
		const points = section.querySelectorAll('.track-record-point');
		const stripHeavySvgFilters = typeof window.matchMedia !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

		circles.forEach(function(circle, i) {
			circle.classList.remove('is-active');
			var circleIndex = circle.getAttribute('data-circle-index') || String(i);

			if (i === 0) {
				var fill1 = circle.querySelector('.track-record-circle__fill-1');
				var fill2 = circle.querySelector('.track-record-circle__fill-2');
				var stroke = circle.querySelector('.track-record-circle__stroke');
				var filterGroup = circle.querySelector('.track-record-circle__filter-group');

				circle.setAttribute('data-state', 'stage5');
				if (stroke) {
					stroke.setAttribute('stroke', 'url(#track-record-stroke-' + circleIndex + '-standard)');
					stroke.setAttribute('stroke-opacity', '0.3');
				}
				if (fill1) {
					fill1.setAttribute('fill', 'url(#track-record-gradient-' + circleIndex + '-first-radial-stage5)');
					fill1.setAttribute('fill-opacity', '0.22');
				}
				if (fill2) {
					fill2.setAttribute('fill', 'url(#track-record-gradient-' + circleIndex + '-first-radial-stage5)');
					fill2.setAttribute('fill-opacity', '0.22');
				}
				if (filterGroup) {
					filterGroup.removeAttribute('filter');
				}
			} else {
				circle.setAttribute('data-state', 'stage2');
				var otherGroup = circle.querySelector('.track-record-circle__other-group');
				var otherStrokeGroup = circle.querySelector('.track-record-circle__other-stroke-group');
				var otherFill = circle.querySelector('.track-record-circle__other-fill');
				var otherStroke = circle.querySelector('.track-record-circle__other-stroke');

				if (otherGroup) {
					otherGroup.setAttribute('opacity', '1');
				}
				if (otherFill) {
					otherFill.setAttribute('fill', 'url(#track-record-gradient-' + circleIndex + '-other-radial-stage2)');
					otherFill.setAttribute('fill-opacity', '0.22');
				}
				if (otherStroke) {
					otherStroke.setAttribute('stroke', 'url(#track-record-stroke-' + circleIndex + '-other-stage2)');
					otherStroke.setAttribute('stroke-opacity', '0.3');
				}
				if (otherStrokeGroup) {
					var isLastCircle = circle.getAttribute('data-is-last') === 'true';
					if (isLastCircle && !stripHeavySvgFilters) {
						otherStrokeGroup.setAttribute('filter', 'url(#track-record-outer-glow-' + circleIndex + ')');
					} else {
						otherStrokeGroup.removeAttribute('filter');
					}
				}
			}

			gsap.set(circle, {
				opacity: 1,
				scale: 1,
				visibility: 'visible',
				pointerEvents: 'none',
			});
		});

		points.forEach(function(point) {
			gsap.set(point, {
				opacity: 1,
				scale: 1,
				xPercent: -50,
				yPercent: -50,
				y: -pointLiftPx,
			});
		});
	}

	function initTrackRecordAnimation() {
		if (typeof gsap === 'undefined') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initTrackRecordAnimation, 100);
			} else {
				console.error('Track Record: GSAP failed to load.');
			}
			return;
		}

		const section = document.getElementById('track-record-section');
		if (!section) {
			return;
		}

		const circlesWrapper = section.querySelector('.track-record-section__circles-wrapper');
		const circles = section.querySelectorAll('.track-record-circle');

		const N = circles.length;
		if (N === 0 || !circlesWrapper) {
			return;
		}

		function isMobile() {
			return window.innerWidth <= 767;
		}

		// Calculate vertical positions based on circle heights
		// First circle: -120px from top on mobile, 100px on desktop/tablet (negative pulls circles up)
		// Each next circle: top[i] = top[i-1] - (height[i] - height[i-1] - 120)
		const initialTop = isMobile() ? -120 : 100;
		let previousTop = initialTop;
		let previousHeight = 0; // Will be set in first iteration
		let lowestBottom = -Infinity;

		// Set vertical positions and sizes for each circle; compute lowest bottom from our values (not DOM) so height is correct on first load
		circles.forEach(function(circle, i) {
			// Get circle size from SVG
			const svg = circle.querySelector('svg');
			if (svg) {
				const circleWidth = parseInt(svg.getAttribute('width')) || 679;
				const circleHeight = parseInt(svg.getAttribute('height')) || 398;

				// Set SVG to use calculated dimensions
				svg.style.width = circleWidth + 'px';
				svg.style.height = circleHeight + 'px';

				// Calculate vertical position
				let currentTop;
				if (i === 0) {
					// First circle: initialTop (-120 on mobile, 100 on desktop)
					currentTop = initialTop;
				} else {
					// Calculate offset: (current_height - previous_height - 120)
					const diffHeight = circleHeight - previousHeight;
					const offset = diffHeight - 120;
					currentTop = previousTop - offset;
				}

				// Set vertical position and z-index
				// z-index formula: N - i, where N is total circles, i is circle index
				// Circle 0 gets z-index N (highest), circle 1 gets N-1, etc.
				gsap.set(circle, {
					top: currentTop,
					zIndex: N - i,
				});

				// Use computed currentTop for height (avoids wrong height on load when DOM/GSAP not yet updated)
				const circleBottom = currentTop + circleHeight;
				if (circleBottom > lowestBottom) {
					lowestBottom = circleBottom;
				}

				// Store for next iteration
				previousTop = currentTop;
				previousHeight = circleHeight;
			}
		});

		// Set section height: top padding + circles + bottom padding; on mobile subtract initialTop offset so negative top doesn't add extra gap below
		if (lowestBottom !== -Infinity) {
			const sectionStyle = window.getComputedStyle(section);
			const paddingTop = parseFloat(sectionStyle.paddingTop) || 0;
			const paddingBottom = parseFloat(sectionStyle.paddingBottom) || 80;
			const mobileBottomOffset = isMobile() ? Math.abs(initialTop) : 0;
			const requiredHeight = paddingTop + lowestBottom + paddingBottom - mobileBottomOffset;
			const finalHeight = isMobile() ? requiredHeight : Math.max(window.innerHeight, requiredHeight);
			// Subpixel / dynamic viewport on iOS: small buffer avoids a visible seam between section fill and content below.
			const mobileHeightBuffer = isMobile() ? 8 : 0;

			gsap.set(section, {
				height: finalHeight + mobileHeightBuffer + 'px',
			});
		}

		applyTrackRecordFinalVisualState(section, TRACK_RECORD_POINT_LIFT_PX);

		window.addEventListener('load', function() {
			applyTrackRecordFinalVisualState(section, TRACK_RECORD_POINT_LIFT_PX);
		});

		// Circle hover effect
		// Store active animations to kill them if needed
		const activeHoverAnimations = new Map();

		circles.forEach(function(circle) {
			circle.addEventListener('mouseenter', function() {
				// Visible when opacity > 0 and visibility !== 'hidden'. Circle container uses pointer-events: none
				// so hits come from children (.track-record-point, .track-record-circle__label); do not gate on container pointer-events.
				const inlineOpacity = circle.style.opacity;
				const inlineVisibility = circle.style.visibility;

				const computedStyle = window.getComputedStyle(circle);
				const opacity = inlineOpacity ? parseFloat(inlineOpacity) : (parseFloat(computedStyle.opacity) || 0);
				const visibility = inlineVisibility || computedStyle.visibility;

				const isVisible = opacity > 0 && visibility !== 'hidden';

				if (!isVisible) {
					return;
				}

				circle.classList.add('is-hovered');

				// Reveal all points for this circle
				const circlePoints = circle.querySelectorAll('.track-record-point');
				circlePoints.forEach(function(point) {
					const pointImage = point.querySelector('.track-record-point__image');
					if (pointImage) {
						// Kill any existing animation for this point image
						const existingAnim = activeHoverAnimations.get(pointImage);
						if (existingAnim) {
							existingAnim.kill();
							activeHoverAnimations.delete(pointImage);
						}

						point.classList.add('is-hovered');
						const anim = gsap.to(pointImage, {
							opacity: 1,
							scale: 1,
							duration: 0.3,
							ease: 'power2.out',
							onComplete: function() {
								// Remove from active animations when complete
								activeHoverAnimations.delete(pointImage);
							}
						});
						activeHoverAnimations.set(pointImage, anim);
					}
				});
			});

			var hoverHideTimeout = null;
			function hideCirclePoints() {
				circle.classList.remove('is-hovered');
				const circlePoints = circle.querySelectorAll('.track-record-point');
				circlePoints.forEach(function(point) {
					const pointImage = point.querySelector('.track-record-point__image');
					if (pointImage) {
						const existingAnim = activeHoverAnimations.get(pointImage);
						if (existingAnim) {
							existingAnim.kill();
							activeHoverAnimations.delete(pointImage);
						}
						point.classList.remove('is-hovered');
						gsap.set(pointImage, { opacity: 0, scale: 0.8 });
					}
				});
			}
			circle.addEventListener('mouseleave', function() {
				hoverHideTimeout = setTimeout(hideCirclePoints, 450);
			});
			circle.addEventListener('mouseenter', function() {
				if (hoverHideTimeout) {
					clearTimeout(hoverHideTimeout);
					hoverHideTimeout = null;
				}
			});

			circle.addEventListener('mouseout', function(e) {
				if (!circle.contains(e.relatedTarget)) {
					if (hoverHideTimeout) {
						clearTimeout(hoverHideTimeout);
					}
					hoverHideTimeout = setTimeout(hideCirclePoints, 450);
				}
			});
		});

		// Point hover effect removed - only circle hover controls popup visibility

		// Adjust point angles for all circles based on viewport and spacing
		function adjustPointAnglesForViewport() {
			// Popup dimensions for margin calculations
			const popupHeight = 58 + (19 * 2); // 96px total (height + padding)
			const minMarginTop = popupHeight + 20; // 116px from top edge for popup
			const minMarginSides = 50; // 50px from side edges
			const minMarginBottom = 20; // 20px from bottom edge

			circles.forEach(function(circle, i) {
				// Get circle index from data attribute (matches PHP circle_index)
				const circleIndex = parseInt(circle.getAttribute('data-circle-index')) || i;
				// Get original index from circles_order for angle calculation logic (if exists)
				// Use original index for angle range check to match PHP logic
				const originalIndexAttr = circle.getAttribute('data-original-index');
				const originalIndex = originalIndexAttr !== null ? parseInt(originalIndexAttr) : circleIndex;

				// Get circle position and size
				const circleRect = circle.getBoundingClientRect();
				const svg = circle.querySelector('svg');
				if (!svg) {
					return;
				}

				const circleWidth = parseInt(svg.getAttribute('width')) || 679;
				const circleHeight = parseInt(svg.getAttribute('height')) || 398;
				const center_x = circleWidth / 2;
				const center_y = circleHeight / 2;
				const radius_x = center_x;
				const radius_y = center_y;

				// Get viewport dimensions
				const viewportHeight = window.innerHeight;
				const viewportWidth = window.innerWidth;

				// Popup dimensions for margin calculations
				const popupHeight = 58 + (19 * 2); // 96px total (height + padding)
				const minMarginTop = popupHeight + 20; // 116px from top edge for popup
				const minMarginSides = 50; // 50px from side edges
				const minMarginBottom = 20; // 20px from bottom edge

				// Get all points for this circle
				const circlePoints = circle.querySelectorAll('.track-record-point');
				const totalPoints = circlePoints.length;

				if (totalPoints === 0) {
					return;
				}

				// Minimum distance between points in pixels (to prevent popup overlap)
				// Increased to ensure better distribution across the circle
				const minPointDistance = 180; // Minimum distance in pixels (increased from 120)
				// Convert to angular distance based on circle radius
				const avgRadius = (radius_x + radius_y) / 2;
				const minAngleSeparation = (minPointDistance / avgRadius) * (180 / Math.PI); // Convert to degrees

				// Use original index for angle range check to match PHP logic (evona_get_track_record_point_angle)
				if (originalIndex <= 1) {
					// For first two circles: two arcs [0,220] and [320,360], total 260° (matches PHP)
					const arc1_start = 0;
					const arc1_end = 220;
					const arc2_start = 320;
					const arc2_end = 360;
					const total_arc = 260; // 220 + 40

					// Store placed points coordinates to check for overlaps
					const placedPoints = [];

					// Linear position 0..260 → angle in [0,220] or [320,360]
					function getAngleFromLinear(linear) {
						if (linear <= arc1_end) {
							return linear;
						}
						return arc2_start + (linear - arc1_end);
					}

					// Ensure angle stays in [0,220] ∪ [320,360]
					function ensureValidAngleTwoArcs(angle) {
						angle = ((angle % 360) + 360) % 360;
						if (angle >= arc1_start && angle <= arc1_end) {
							return angle;
						}
						if (angle >= arc2_start && angle <= arc2_end) {
							return angle;
						}
						if (angle > arc1_end && angle < arc2_start) {
							return angle < 270 ? arc1_end : arc2_start;
						}
						return arc1_start;
					}

					// Helper function to calculate distance between two points in screen coordinates
					function getDistance(x1, y1, x2, y2) {
						const dx = x2 - x1;
						const dy = y2 - y1;
						return Math.sqrt(dx * dx + dy * dy);
					}

					// Helper function to check if position is too close to any placed point
					function isTooCloseToPlacedPoints(screenX, screenY) {
						for (let i = 0; i < placedPoints.length; i++) {
							const distance = getDistance(screenX, screenY, placedPoints[i].screenX, placedPoints[i].screenY);
							if (distance < minPointDistance) {
								return true;
							}
						}
						return false;
					}

					// Find valid position over two arcs: iterate linear 0..260, map to angle, check viewport/placed
					function findValidPositionTwoArcs(maxAttempts, pointIndex, totalPoints) {
						maxAttempts = maxAttempts || 300;
						pointIndex = pointIndex != null ? pointIndex : 0;
						totalPoints = totalPoints != null ? totalPoints : 1;
						// Use same edge margins as in main distribution
						const edgeMargin = total_arc * 0.08;
						const usableRange = total_arc - (edgeMargin * 2);
						const preferredLinear = totalPoints <= 1 ? edgeMargin : edgeMargin + (pointIndex / Math.max(1, totalPoints - 1)) * usableRange;
						const preferredAngle = getAngleFromLinear(preferredLinear);

						const candidates = [];
						const linearStep = total_arc / (maxAttempts - 1);
						for (let k = 0; k < maxAttempts; k++) {
							const linear = k * linearStep;
							const testAngle = getAngleFromLinear(linear);
							const angle_rad = testAngle * (Math.PI / 180);
							const test_x = center_x + (radius_x * Math.cos(angle_rad));
							const test_y = center_y + (radius_y * Math.sin(angle_rad));
							const testScreenX = circleRect.left + (test_x / circleWidth) * circleRect.width;
							const testScreenY = circleRect.top + (test_y / circleHeight) * circleRect.height;

							if (testScreenY < minMarginTop || testScreenX < minMarginSides || testScreenX > viewportWidth - minMarginSides) {
								continue;
							}
							if (!isTooCloseToPlacedPoints(testScreenX, testScreenY)) {
								let minDistanceToPlaced = Infinity;
								for (let i = 0; i < placedPoints.length; i++) {
									const dist = getDistance(testScreenX, testScreenY, placedPoints[i].screenX, placedPoints[i].screenY);
									if (dist < minDistanceToPlaced) {
										minDistanceToPlaced = dist;
									}
								}
								candidates.push({
									angle: testAngle,
									x: test_x,
									y: test_y,
									screenX: testScreenX,
									screenY: testScreenY,
									minDistance: minDistanceToPlaced,
									preferredPenalty: 1 / (1 + Math.abs(testAngle - preferredAngle) / 90)
								});
							}
						}
						if (candidates.length > 0) {
							// Bonus for central positions in arcs (prefer arc center over edges)
							const arc1Center = (arc1_start + arc1_end) / 2; // 110°
							const arc2Center = (arc2_start + arc2_end) / 2; // 340°
							candidates.forEach(function(c) {
								let centerBonus = 1.0;
								if (c.angle >= arc1_start && c.angle <= arc1_end) {
									// Bonus for proximity to first arc center
									const distFromCenter = Math.abs(c.angle - arc1Center);
									centerBonus = 1.0 + (1.0 - distFromCenter / (arc1_end - arc1_start) * 0.5) * 0.3; // Up to 30% bonus
								} else if (c.angle >= arc2_start && c.angle <= arc2_end) {
									// Bonus for proximity to second arc center
									const distFromCenter = Math.abs(c.angle - arc2Center);
									centerBonus = 1.0 + (1.0 - distFromCenter / (arc2_end - arc2_start) * 0.5) * 0.3; // Up to 30% bonus
								}
								c.score = c.minDistance * c.preferredPenalty * centerBonus;
							});
							candidates.sort(function(a, b) {
								return b.score - a.score;
							});
							return candidates[0];
						}
						const fallbackAngle = ensureValidAngleTwoArcs(preferredAngle);
						const fallbackAngleRad = fallbackAngle * (Math.PI / 180);
						const fallback_x = center_x + (radius_x * Math.cos(fallbackAngleRad));
						const fallback_y = center_y + (radius_y * Math.sin(fallbackAngleRad));
						return {
							angle: fallbackAngle,
							x: fallback_x,
							y: fallback_y,
							screenX: circleRect.left + (fallback_x / circleWidth) * circleRect.width,
							screenY: circleRect.top + (fallback_y / circleHeight) * circleRect.height
						};
					}

					// Optimal step in linear space (0..260)
					const optimalLinearStep = totalPoints > 1 ? total_arc / (totalPoints - 1) : 0;
					const totalMinSeparationNeeded = minAngleSeparation * (totalPoints - 1);
					const actualLinearStep = totalMinSeparationNeeded <= total_arc ? optimalLinearStep : minAngleSeparation;
					if (totalMinSeparationNeeded > total_arc) {
						console.warn('Warning: Too many points for two arcs. Using min separation.');
					}

					circlePoints.forEach(function(point, pointIndex) {
						const phpAngle = point.getAttribute('data-angle');
						let newAngle;
						let initialAngle;
						let isUsingSavedAngle = false;

						// Try to use saved angle from PHP if valid
						const savedAngle = phpAngle ? parseFloat(phpAngle) : null;
						const hasValidSavedAngle = savedAngle !== null && !isNaN(savedAngle) && savedAngle >= 0 && savedAngle <= 360;

						if (hasValidSavedAngle) {
							// Check if saved angle is in valid range for two arcs: [0,220] ∪ [320,360]
							const isInArc1 = savedAngle >= arc1_start && savedAngle <= arc1_end;
							const isInArc2 = savedAngle >= arc2_start && savedAngle <= arc2_end;

							if (isInArc1 || isInArc2) {
								// Saved angle is in valid range, use it
								newAngle = savedAngle;
								initialAngle = savedAngle;
								isUsingSavedAngle = true;
							} else {
								// Saved angle is not in valid range, use auto-calculation
								const edgeMargin = total_arc * 0.08;
								const usableRange = total_arc - (edgeMargin * 2);
								const linear = totalPoints <= 1 ? edgeMargin : edgeMargin + (pointIndex / (totalPoints - 1)) * usableRange;
								newAngle = getAngleFromLinear(linear);
								initialAngle = newAngle;
							}
						} else {
							// No valid saved angle, use auto-calculation based on index
							const edgeMargin = total_arc * 0.08;
							const usableRange = total_arc - (edgeMargin * 2);
							const linear = totalPoints <= 1 ? edgeMargin : edgeMargin + (pointIndex / (totalPoints - 1)) * usableRange;
							newAngle = getAngleFromLinear(linear);
							initialAngle = newAngle;
						}

						const angle_rad = newAngle * (Math.PI / 180);
						let point_x = center_x + (radius_x * Math.cos(angle_rad));
						let point_y = center_y + (radius_y * Math.sin(angle_rad));
						let pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
						let pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;

						// Viewport/margin adjustments — keep angle in [0,220] ∪ [320,360]
						// For saved angles, only apply critical adjustments (when point is really outside viewport)
						// For auto-calculated angles, apply normal adjustments

						// Only apply viewport adjustments if point is critically outside bounds
						// For saved angles, use stricter threshold to preserve user's choice
						const criticalMarginTop = isUsingSavedAngle ? minMarginTop - 50 : minMarginTop; // More lenient for saved angles
						const criticalMarginSides = isUsingSavedAngle ? minMarginSides - 30 : minMarginSides; // More lenient for saved angles

						// For saved angles, skip viewport adjustments unless point is critically outside
						// This preserves user's choice of angle
						if (!isUsingSavedAngle && pointScreenY < criticalMarginTop) {
							const step = 5;
							const towardAngle = 180; // bottom of arc
							while (pointScreenY < minMarginTop) {
								if (Math.abs(newAngle - towardAngle) < step) {
									break;
								}
								newAngle = newAngle < towardAngle ? Math.min(newAngle + step, towardAngle) : Math.max(newAngle - step, towardAngle);
								newAngle = ensureValidAngleTwoArcs(newAngle);
								const newAngleRad = newAngle * (Math.PI / 180);
								point_x = center_x + (radius_x * Math.cos(newAngleRad));
								point_y = center_y + (radius_y * Math.sin(newAngleRad));
								pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
								pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
							}
							if (pointScreenY < criticalMarginTop) {
								const minY = criticalMarginTop - circleRect.top;
								const minYInCircle = (minY / circleRect.height) * circleHeight;
								const sinValue = (minYInCircle - center_y) / radius_y;
								if (sinValue >= -1 && sinValue <= 1) {
									const angleForY = Math.asin(sinValue) * (180 / Math.PI);
									newAngle = angleForY <= 90 ? 180 - angleForY : (angleForY <= 180 ? angleForY : 180 + (180 - angleForY));
									newAngle = ensureValidAngleTwoArcs(newAngle);
									const newAngleRad = newAngle * (Math.PI / 180);
									point_x = center_x + (radius_x * Math.cos(newAngleRad));
									point_y = center_y + (radius_y * Math.sin(newAngleRad));
									pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
									pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								}
							}
						}
						// Soft side-margin checks: shift minimally, prefer central positions in arcs
						// For saved angles, completely skip side margin adjustments to preserve user's choice
						if (!isUsingSavedAngle && pointScreenX < criticalMarginSides) {
							// If point in first arc [0,220], shift toward arc center (110°), at most 30% of current position
							if (newAngle >= arc1_start && newAngle <= arc1_end) {
								const arc1Center = (arc1_start + arc1_end) / 2; // 110°
								const maxShift = (arc1_end - arc1_start) * 0.3; // 30% of arc range
								const targetAngle = Math.min(arc1Center, newAngle + maxShift);
								if (targetAngle > newAngle) {
									newAngle = ensureValidAngleTwoArcs(targetAngle);
									const newAngleRad = newAngle * (Math.PI / 180);
									point_x = center_x + (radius_x * Math.cos(newAngleRad));
									point_y = center_y + (radius_y * Math.sin(newAngleRad));
									pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
									pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								}
							}
							// If still too close, use findValidPosition for auto-calculated angles only
							if (pointScreenX < criticalMarginSides) {
								const validPos = findValidPositionTwoArcs(300, pointIndex, totalPoints);
								if (validPos && validPos.screenX >= criticalMarginSides) {
									newAngle = validPos.angle;
									point_x = validPos.x;
									point_y = validPos.y;
									pointScreenX = validPos.screenX;
									pointScreenY = validPos.screenY;
								}
							}
						} else if (!isUsingSavedAngle && pointScreenX > viewportWidth - criticalMarginSides) {
							// If point in second arc [320,360], shift toward arc center (340°), at most 30% of current position
							if (newAngle >= arc2_start && newAngle <= arc2_end) {
								const arc2Center = (arc2_start + arc2_end) / 2; // 340°
								const maxShift = (arc2_end - arc2_start) * 0.3; // 30% of arc range
								const targetAngle = Math.max(arc2Center, newAngle - maxShift);
								if (targetAngle < newAngle) {
									newAngle = ensureValidAngleTwoArcs(targetAngle);
									const newAngleRad = newAngle * (Math.PI / 180);
									point_x = center_x + (radius_x * Math.cos(newAngleRad));
									point_y = center_y + (radius_y * Math.sin(newAngleRad));
									pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
									pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								}
							}
							// If still too close, use findValidPosition for auto-calculated angles only
							if (pointScreenX > viewportWidth - criticalMarginSides) {
								const validPos = findValidPositionTwoArcs(300, pointIndex, totalPoints);
								if (validPos && validPos.screenX <= viewportWidth - criticalMarginSides) {
									newAngle = validPos.angle;
									point_x = validPos.x;
									point_y = validPos.y;
									pointScreenX = validPos.screenX;
									pointScreenY = validPos.screenY;
								}
							}
						}

						// For saved angles, skip overlap checks to preserve user's choice
						// Only check overlaps for auto-calculated angles
						if (!isUsingSavedAngle && isTooCloseToPlacedPoints(pointScreenX, pointScreenY)) {
							const validPos = findValidPositionTwoArcs(300, pointIndex, totalPoints);
							if (validPos) {
								newAngle = validPos.angle;
								point_x = validPos.x;
								point_y = validPos.y;
								pointScreenX = validPos.screenX;
								pointScreenY = validPos.screenY;
							}
						}

						newAngle = ensureValidAngleTwoArcs(newAngle);

						placedPoints.push({
							screenX: pointScreenX,
							screenY: pointScreenY
						});

						point.setAttribute('data-angle', newAngle.toString());
						// If point has per-viewport CSS variables (desktop/tablet/mobile), do not set left/top so CSS media queries apply
						if (!point.style.getPropertyValue('--tr-left-d') && !point.style.getPropertyValue('--tr-left-t')) {
							gsap.set(point, {
								left: (point_x / circleWidth) * 100 + '%',
								top: (point_y / circleHeight) * 100 + '%',
								xPercent: -50,
								yPercent: -50,
								y: -TRACK_RECORD_POINT_LIFT_PX,
							});
						}
					});
				} else if (originalIndex >= 2) {
					// Single-arc ranges per circle (matches PHP evona_get_track_record_point_angle): 2→[30,150], 3→[50,130], 4→[60,120], 5→[70,110], 6+→[75,105]
					const SINGLE_ARC_RANGES = {
						2: [30, 150],
						3: [50, 130],
						4: [60, 120],
						5: [70, 110],
						6: [75, 105]
					};
					const rangeConfig = SINGLE_ARC_RANGES[Math.min(originalIndex, 6)] || [75, 105];
					const range_start = rangeConfig[0];
					const range_end = rangeConfig[1];
					const range_span = range_end - range_start;

					const placedPoints = [];

					function getDistance(x1, y1, x2, y2) {
						const dx = x2 - x1;
						const dy = y2 - y1;
						return Math.sqrt(dx * dx + dy * dy);
					}

					function isTooCloseToPlacedPoints(screenX, screenY) {
						for (let i = 0; i < placedPoints.length; i++) {
							const d = getDistance(screenX, screenY, placedPoints[i].screenX, placedPoints[i].screenY);
							if (d < minPointDistance) return true;
						}
						return false;
					}

					function findValidPositionSingleArc(maxAttempts, pointIndex, totalPoints) {
						maxAttempts = maxAttempts || 200;
						pointIndex = pointIndex != null ? pointIndex : 0;
						totalPoints = totalPoints != null ? totalPoints : 1;
						// Use same edge margins as in main distribution
						const edgeMargin = range_span * 0.10;
						const usableRange = range_span - (edgeMargin * 2);
						const t = totalPoints <= 1 ? 0.5 : pointIndex / Math.max(1, totalPoints - 1);
						const preferredAngle = range_start + edgeMargin + t * usableRange;
						const candidates = [];
						const angleStep = range_span / (maxAttempts - 1);
						for (let k = 0; k < maxAttempts; k++) {
							const testAngle = range_start + k * angleStep;
							if (testAngle > range_end) continue;
							const angle_rad = testAngle * (Math.PI / 180);
							const test_x = center_x + (radius_x * Math.cos(angle_rad));
							const test_y = center_y + (radius_y * Math.sin(angle_rad));
							const testScreenX = circleRect.left + (test_x / circleWidth) * circleRect.width;
							const testScreenY = circleRect.top + (test_y / circleHeight) * circleRect.height;
							if (testScreenY < minMarginTop || testScreenX < minMarginSides || testScreenX > viewportWidth - minMarginSides) continue;
							if (!isTooCloseToPlacedPoints(testScreenX, testScreenY)) {
								let minD = Infinity;
								for (let i = 0; i < placedPoints.length; i++) {
									const dist = getDistance(testScreenX, testScreenY, placedPoints[i].screenX, placedPoints[i].screenY);
									if (dist < minD) minD = dist;
								}
								candidates.push({
									angle: testAngle,
									x: test_x,
									y: test_y,
									screenX: testScreenX,
									screenY: testScreenY,
									minDistance: minD,
									preferredPenalty: 1 / (1 + Math.abs(testAngle - preferredAngle) / 90)
								});
							}
						}
						if (candidates.length > 0) {
							// Bonus for central positions in range (prefer center over edges)
							const centerAngle = (range_start + range_end) / 2;
							candidates.forEach(function(c) {
								const distFromCenter = Math.abs(c.angle - centerAngle);
								const centerBonus = 1.0 + (1.0 - distFromCenter / range_span) * 0.4; // Up to 40% bonus for central positions
								c.score = c.minDistance * c.preferredPenalty * centerBonus;
							});
							candidates.sort(function(a, b) { return b.score - a.score; });
							return candidates[0];
						}
						const fa = Math.max(range_start, Math.min(range_end, preferredAngle));
						const far = fa * (Math.PI / 180);
						return {
							angle: fa,
							x: center_x + (radius_x * Math.cos(far)),
							y: center_y + (radius_y * Math.sin(far)),
							screenX: circleRect.left + ((center_x + (radius_x * Math.cos(far))) / circleWidth) * circleRect.width,
							screenY: circleRect.top + ((center_y + (radius_y * Math.sin(far))) / circleHeight) * circleRect.height
						};
					}

					function clampAngle(a) {
						return Math.max(range_start, Math.min(range_end, ((a % 360) + 360) % 360));
					}

					const optimalStep = totalPoints > 1 ? range_span / (totalPoints - 1) : 0;
					const totalMinSep = minAngleSeparation * (totalPoints - 1);
					if (totalMinSep > range_span) {
						console.warn('Warning: Too many points for single arc [' + range_start + ',' + range_end + ']. Using min separation.');
					}

					circlePoints.forEach(function(point, pointIndex) {
						const phpAngle = point.getAttribute('data-angle');
						let newAngle;
						let initialAngle;

						// Try to use saved angle from PHP if valid
						const savedAngle = phpAngle ? parseFloat(phpAngle) : null;
						const hasValidSavedAngle = savedAngle !== null && !isNaN(savedAngle) && savedAngle >= 0 && savedAngle <= 360;

						if (hasValidSavedAngle) {
							// Check if saved angle is in valid range for this circle: [range_start, range_end]
							if (savedAngle >= range_start && savedAngle <= range_end) {
								// Saved angle is in valid range, use it
								newAngle = savedAngle;
								initialAngle = savedAngle;
							} else {
								// Saved angle is not in valid range, use auto-calculation
								const edgeMargin = range_span * 0.10;
								const usableRange = range_span - (edgeMargin * 2);
								const t = totalPoints <= 1 ? 0.5 : pointIndex / (totalPoints - 1);
								newAngle = range_start + edgeMargin + t * usableRange;
								initialAngle = newAngle;
							}
						} else {
							// No valid saved angle, use auto-calculation based on index
							const edgeMargin = range_span * 0.10;
							const usableRange = range_span - (edgeMargin * 2);
							const t = totalPoints <= 1 ? 0.5 : pointIndex / (totalPoints - 1);
							newAngle = range_start + edgeMargin + t * usableRange;
							initialAngle = newAngle;
						}

						let angle_rad = newAngle * (Math.PI / 180);
						let point_x = center_x + (radius_x * Math.cos(angle_rad));
						let point_y = center_y + (radius_y * Math.sin(angle_rad));
						let pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
						let pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;

						// Soft viewport checks: shift only when needed, prefer central positions
						const centerAngle = (range_start + range_end) / 2;
						let needsAdjustment = false;

						// Top margin check: shift only if point is too high
						if (pointScreenY < minMarginTop) {
							// Smooth shift toward range center (usually lower part of arc)
							const targetAngle = centerAngle; // Range center is usually at bottom
							const maxAdjustment = Math.min(30, range_span * 0.3); // Max shift 30° or 30% of range
							const angleDiff = targetAngle - newAngle;
							if (Math.abs(angleDiff) > 0.1) {
								const adjustment = Math.sign(angleDiff) * Math.min(Math.abs(angleDiff), maxAdjustment);
								newAngle = clampAngle(newAngle + adjustment);
								angle_rad = newAngle * (Math.PI / 180);
								point_x = center_x + (radius_x * Math.cos(angle_rad));
								point_y = center_y + (radius_y * Math.sin(angle_rad));
								pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
								pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								needsAdjustment = true;
							}
							// If still too high, use findValidPosition
							if (pointScreenY < minMarginTop) {
								const vp = findValidPositionSingleArc(200, pointIndex, totalPoints);
								if (vp && vp.screenY >= minMarginTop) {
									newAngle = vp.angle;
									point_x = vp.x;
									point_y = vp.y;
									pointScreenX = vp.screenX;
									pointScreenY = vp.screenY;
								}
							}
						}

						// Side margin check: shift minimally, only when critical
						if (pointScreenX < minMarginSides) {
							// Shift right, at most 20% from range center
							const safeAngle = Math.max(newAngle, centerAngle - range_span * 0.2);
							if (safeAngle > newAngle) {
								newAngle = clampAngle(safeAngle);
								angle_rad = newAngle * (Math.PI / 180);
								point_x = center_x + (radius_x * Math.cos(angle_rad));
								point_y = center_y + (radius_y * Math.sin(angle_rad));
								pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
								pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								needsAdjustment = true;
							}
							// If still too close to edge, use findValidPosition
							if (pointScreenX < minMarginSides) {
								const vp = findValidPositionSingleArc(200, pointIndex, totalPoints);
								if (vp && vp.screenX >= minMarginSides) {
									newAngle = vp.angle;
									point_x = vp.x;
									point_y = vp.y;
									pointScreenX = vp.screenX;
									pointScreenY = vp.screenY;
								}
							}
						} else if (pointScreenX > viewportWidth - minMarginSides) {
							// Shift left, at most 20% from range center
							const safeAngle = Math.min(newAngle, centerAngle + range_span * 0.2);
							if (safeAngle < newAngle) {
								newAngle = clampAngle(safeAngle);
								angle_rad = newAngle * (Math.PI / 180);
								point_x = center_x + (radius_x * Math.cos(angle_rad));
								point_y = center_y + (radius_y * Math.sin(angle_rad));
								pointScreenX = circleRect.left + (point_x / circleWidth) * circleRect.width;
								pointScreenY = circleRect.top + (point_y / circleHeight) * circleRect.height;
								needsAdjustment = true;
							}
							// If still too close to edge, use findValidPosition
							if (pointScreenX > viewportWidth - minMarginSides) {
								const vp = findValidPositionSingleArc(200, pointIndex, totalPoints);
								if (vp && vp.screenX <= viewportWidth - minMarginSides) {
									newAngle = vp.angle;
									point_x = vp.x;
									point_y = vp.y;
									pointScreenX = vp.screenX;
									pointScreenY = vp.screenY;
								}
							}
						}

						// Min distance between points check: use smart search
						if (isTooCloseToPlacedPoints(pointScreenX, pointScreenY)) {
							const vp = findValidPositionSingleArc(300, pointIndex, totalPoints);
							if (vp) {
								newAngle = vp.angle;
								point_x = vp.x;
								point_y = vp.y;
								pointScreenX = vp.screenX;
								pointScreenY = vp.screenY;
							}
						}

						newAngle = clampAngle(newAngle);
						placedPoints.push({ screenX: pointScreenX, screenY: pointScreenY });

						point.setAttribute('data-angle', newAngle.toString());
						// If point has per-viewport CSS variables (desktop/tablet/mobile), do not set left/top so CSS media queries apply
						if (!point.style.getPropertyValue('--tr-left-d') && !point.style.getPropertyValue('--tr-left-t')) {
							gsap.set(point, {
								left: (point_x / circleWidth) * 100 + '%',
								top: (point_y / circleHeight) * 100 + '%',
								xPercent: -50,
								yPercent: -50,
								y: -TRACK_RECORD_POINT_LIFT_PX,
							});
						}
					});
				}
			});
		}

		// Adjust point angles on initial load
		setTimeout(function() {
			adjustPointAnglesForViewport();
		}, 100);

		// Resize: recalculate positions, re-apply static visuals, refresh point layout
		let resizeTimer;
		window.addEventListener('resize', function() {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(function() {
				// Recalculate vertical positions on resize
				// First circle: -120px from top on mobile, 100px on desktop/tablet
				// Each next circle: top[i] = top[i-1] - (height[i] - height[i-1] - 120)
				const initialTop = isMobile() ? -120 : 100;
				let newPreviousTop = initialTop;
				let newPreviousHeight = 0;
				let newLowestBottom = -Infinity;

				circles.forEach(function(circle, i) {
					const svg = circle.querySelector('svg');
					if (svg) {
						const circleHeight = parseInt(svg.getAttribute('height')) || 398;

						let newCurrentTop;
						if (i === 0) {
							// First circle: initialTop (-120 on mobile, 100 on desktop)
							newCurrentTop = initialTop;
						} else {
							// Calculate offset: (current_height - previous_height - 120)
							const diffHeight = circleHeight - newPreviousHeight;
							const offset = diffHeight - 120;
							newCurrentTop = newPreviousTop - offset;
						}

						gsap.set(circle, {
							top: newCurrentTop,
							zIndex: N - i,
						});

						// Use computed newCurrentTop for height (same as init)
						const circleBottom = newCurrentTop + circleHeight;
						if (circleBottom > newLowestBottom) {
							newLowestBottom = circleBottom;
						}

						// Store for next iteration
						newPreviousTop = newCurrentTop;
						newPreviousHeight = circleHeight;
					}
				});

				// Set section height: on mobile subtract initialTop offset so negative top doesn't add extra gap below
				if (newLowestBottom !== -Infinity) {
					const sectionStyle = window.getComputedStyle(section);
					const paddingTop = parseFloat(sectionStyle.paddingTop) || 0;
					const paddingBottom = parseFloat(sectionStyle.paddingBottom) || 80;
					const mobileBottomOffset = isMobile() ? Math.abs(initialTop) : 0;
					const requiredHeight = paddingTop + newLowestBottom + paddingBottom - mobileBottomOffset;
					const finalHeight = isMobile() ? requiredHeight : Math.max(window.innerHeight, requiredHeight);
					const mobileHeightBuffer = isMobile() ? 8 : 0;

					gsap.set(section, {
						height: finalHeight + mobileHeightBuffer + 'px',
					});
				}

				applyTrackRecordFinalVisualState(section, TRACK_RECORD_POINT_LIFT_PX);

				// Recalculate point angles for viewport after resize
				adjustPointAnglesForViewport();
			}, 250);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTrackRecordAnimation);
	} else {
		initTrackRecordAnimation();
	}
})();
